import React from 'react';
import { ApolloProvider } from '@apollo/client';
import { Route, AnalyticsProvider } from '@modernhealth/web-analytics';
import { BrowserRouter as Router, Switch } from 'react-router-dom';
import { ToastContainer } from '@modernhealth/vital-components';
// The design system input controls depend on moment being accessible.
import 'moment';

import 'normalize.css/normalize.css';
import './styles/index.scss';
import '@modernhealth/vital-components/dist/style.css';

import { useApollo } from './config/apollo';
import { analyticsClient } from './shared/services/analytics';
import Navbar from './shared/components/Navbar';
import HomePage from './modules/home/pages/Home';
import SeriesPage from './modules/circles/pages/Series';
import TopicPage from './modules/circles/pages/Topic';
import AboutCircles from './modules/circles/pages/AboutCircles';
import RsvpPage from './modules/circles/pages/Rsvp';
import RegisterMembershipCheck from './modules/circles/pages/RegisterMembershipCheck';
import RsvpConfirmation from './modules/circles/pages/RsvpConfirmation';
import RsvpCancellation from './modules/circles/pages/RsvpCancellation';
import RsvpEmailToCalendar from './modules/circles/pages/RsvpEmailToCalendar';
import CodeOfConduct from './modules/circles/pages/CommunityGuidelines';
import NotFoundPage from './modules/404/pages/NotFound';
import UpcomingCirclesPage from './modules/circles/pages/UpcomingCircles';
import Footer from './shared/components/Footer';
import { ScreenWrapper } from './shared/components/ScreenWrapper';
import { TranslationProvider } from './shared/intl/translations';
import { RouteProvider } from './shared/contexts/RouteContext';
import { Replace } from './shared/components/Replace';

const App: React.FC = () => {
  const apolloClient = useApollo({});

  return (
    <Router>
      <TranslationProvider>
        <AnalyticsProvider analyticsClient={analyticsClient}>
          <RouteProvider>
            <ScreenWrapper>
              <ToastContainer limit={1} />
              <Navbar />
              <ApolloProvider client={apolloClient}>
                <Switch>
                  <Route
                    component={SeriesPage}
                    path="/series/:seriesSlug"
                    analytics={{ pageName: 'circles-series' }}
                  />
                  <Route path="/upcoming" analytics={{ pageName: 'circles-upcoming' }}>
                    <UpcomingCirclesPage />
                  </Route>
                  <Route
                    component={AboutCircles}
                    path="/about-circles"
                    analytics={{ pageName: 'circles-about' }}
                  />
                  <Route
                    component={RegisterMembershipCheck}
                    exact
                    path="/circle/:topicSlug/session/:sessionId/membership"
                    analytics={{ pageName: 'circles-membership-check' }}
                  />

                  <Replace
                    from="/circle/:topicSlug/session/:sessionId/register/confirmation/:rsvpId"
                    to="/circle/:topicSlug/session/:sessionId/registered/:rsvpId/confirmation"
                  />
                  <Route
                    component={RsvpConfirmation}
                    path="/circle/:topicSlug/session/:sessionId/registered/:rsvpId/confirmation"
                    analytics={{ pageName: 'circles-confirmation' }}
                  />
                  <Replace
                    from="/circle/:topicSlug/rsvp/:sessionId"
                    to="/circle/:topicSlug/session/:sessionId/register"
                  />
                  <Route
                    component={RsvpPage}
                    exact
                    path="/circle/:topicSlug/session/:sessionId/register"
                    analytics={{ pageName: 'circles-registration-form' }}
                  />
                  <Route
                    component={TopicPage}
                    path="/circle/:topicSlug"
                    analytics={{ pageName: 'circles-topic' }}
                  />
                  <Route
                    component={RsvpEmailToCalendar}
                    exact
                    path="/rsvp/:rsvpId/add-to-calendar"
                    analytics={{ pageName: 'circles-add-to-calendar' }}
                  />
                  <Route
                    component={RsvpCancellation}
                    path="/rsvp/:rsvpId/cancel"
                    analytics={{ pageName: 'circles-cancellation' }}
                  />
                  <Route
                    path="/code-of-conduct"
                    analytics={{ pageName: 'circles-community-guidelines' }}
                  >
                    <CodeOfConduct />
                  </Route>

                  <Route component={HomePage} path="/" exact analytics={{ pageName: 'circles' }} />
                  <Route path="*" analytics={{ pageName: 'not-found' }}>
                    <NotFoundPage />
                  </Route>
                </Switch>
              </ApolloProvider>
            </ScreenWrapper>
            <Footer />
          </RouteProvider>
        </AnalyticsProvider>
      </TranslationProvider>
    </Router>
  );
};

export default App;
